import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';

/**
 * "Jadwalkan Demo" — a LEAD-CAPTURE flow, not self-service tenant creation.
 * Deliberately does NOT create a Supabase Auth user, tenant, or any backend
 * record: ALEX ERP has no public sign-up surface by design (owner-provisioned
 * tenants only). This form collects the minimum qualifying info and hands off
 * to the owner's WhatsApp with a prefilled message; the owner (a human)
 * decides whether/how to provision a real tenant afterward.
 *
 * NEEDS_OWNER_ACTION: OWNER_WHATSAPP_NUMBER below is a placeholder. No real
 * production WhatsApp number has been supplied or approved by the owner, so
 * this intentionally will not deep-link anywhere real until the owner
 * provides the number (see the on-screen notice shown in place of the
 * WhatsApp button while the placeholder is active).
 *
 * STATUS: IMPLEMENTED_UNVERIFIED — see docs/data-dictionary.md §8k.
 */
const OWNER_WHATSAPP_NUMBER: string | null = null; // NEEDS_OWNER_ACTION — e.g. "62812xxxxxxx"

const BUSINESS_TYPES = ['Restoran / F&B', 'Retail', 'Manufaktur', 'Distribusi / Grosir', 'Jasa', 'Lainnya'];

interface LeadForm {
  name: string;
  businessName: string;
  whatsapp: string;
  city: string;
  branchCount: string;
  businessType: string;
  notes: string;
}

const EMPTY_FORM: LeadForm = { name: '', businessName: '', whatsapp: '', city: '', branchCount: '', businessType: BUSINESS_TYPES[0], notes: '' };

function buildWhatsappMessage(form: LeadForm): string {
  return [
    'Halo, saya ingin menjadwalkan demo ALEX ERP.',
    `Nama: ${form.name}`,
    `Nama usaha: ${form.businessName}`,
    `Kota: ${form.city}`,
    `Jumlah cabang: ${form.branchCount}`,
    `Jenis usaha: ${form.businessType}`,
    form.notes ? `Kebutuhan singkat: ${form.notes}` : null,
  ]
    .filter(Boolean)
    .join('\n');
}

export default function ScheduleDemo() {
  const [form, setForm] = useState<LeadForm>(EMPTY_FORM);
  const [submitted, setSubmitted] = useState(false);

  const update = (field: keyof LeadForm) => (e: { target: { value: string } }) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
  };

  const whatsappHref = OWNER_WHATSAPP_NUMBER
    ? `https://wa.me/${OWNER_WHATSAPP_NUMBER}?text=${encodeURIComponent(buildWhatsappMessage(form))}`
    : null;

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', fontFamily: 'var(--alex-font-family)', background: 'var(--alex-app-bg)' }}>
      <header
        style={{
          height: 'var(--alex-topbar-height)',
          background: 'var(--alex-sidebar-bg-deep)',
          display: 'flex',
          alignItems: 'center',
          padding: '0 32px',
          flexShrink: 0,
        }}
      >
        <div style={{ fontWeight: 800, fontSize: 19, letterSpacing: 0.3 }}>
          <span style={{ color: '#fff' }}>ALEX</span> <span style={{ color: 'var(--alex-primary)' }}>ERP</span>
          <span style={{ color: 'var(--alex-sidebar-text-muted)', fontWeight: 500, fontSize: 12, marginLeft: 10 }}>EL Amos Web ERP</span>
        </div>
      </header>

      <main style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
        <div className="alex-card" style={{ width: 460, padding: 40 }}>
          <h1 style={{ fontSize: 22, fontWeight: 800, margin: '0 0 4px', color: 'var(--alex-text-primary)' }}>Jadwalkan Demo</h1>
          <p style={{ fontSize: 'var(--alex-fs-body)', color: 'var(--alex-text-secondary)', margin: '0 0 24px' }}>
            ALEX ERP disiapkan per tenant oleh tim kami — bukan pendaftaran mandiri. Isi form ini, tim kami akan menghubungi Anda untuk demo.
          </p>

          {!submitted ? (
            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <Field label="Nama" value={form.name} onChange={update('name')} required autoFocus />
              <Field label="Nama usaha" value={form.businessName} onChange={update('businessName')} required />
              <Field label="Nomor WhatsApp" value={form.whatsapp} onChange={update('whatsapp')} type="tel" required />
              <Field label="Kota" value={form.city} onChange={update('city')} required />
              <Field label="Jumlah cabang" value={form.branchCount} onChange={update('branchCount')} type="number" required />
              <label style={labelStyle}>
                Jenis usaha
                <select value={form.businessType} onChange={update('businessType')} style={inputStyle}>
                  {BUSINESS_TYPES.map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </select>
              </label>
              <label style={labelStyle}>
                Kebutuhan singkat (opsional)
                <textarea value={form.notes} onChange={update('notes')} rows={3} style={{ ...inputStyle, resize: 'vertical' as const }} />
              </label>
              <button type="submit" style={primaryButtonStyle}>
                Lanjutkan
              </button>
            </form>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div
                style={{
                  background: 'var(--alex-primary-soft)',
                  border: '1px solid var(--alex-primary-border)',
                  borderRadius: 'var(--alex-control-radius)',
                  padding: '14px 16px',
                  fontSize: 'var(--alex-fs-body)',
                  color: 'var(--alex-text-primary)',
                }}
              >
                Terima kasih, {form.name || 'Anda'}. Kirim detail ini ke tim kami melalui WhatsApp untuk menjadwalkan demo.
              </div>
              {whatsappHref ? (
                <a
                  href={whatsappHref}
                  target="_blank"
                  rel="noreferrer"
                  style={{ ...primaryButtonStyle, textAlign: 'center', textDecoration: 'none', display: 'block' }}
                >
                  Lanjutkan via WhatsApp
                </a>
              ) : (
                <div
                  role="status"
                  style={{
                    background: 'var(--alex-warning-soft, #fff7e6)',
                    border: '1px solid var(--alex-warning-border, #f0c36d)',
                    borderRadius: 'var(--alex-control-radius)',
                    padding: '14px 16px',
                    fontSize: 'var(--alex-fs-caption)',
                    color: 'var(--alex-text-primary)',
                  }}
                >
                  Nomor WhatsApp tim ALEX ERP belum dikonfigurasi (NEEDS_OWNER_ACTION). Silakan hubungi kami melalui{' '}
                  <a href="mailto:support@alex-erp.dev">support@alex-erp.dev</a> untuk sementara, sertakan detail yang sudah Anda isi.
                </div>
              )}
            </div>
          )}

          <div style={{ marginTop: 24, textAlign: 'center', fontSize: 'var(--alex-fs-caption)' }}>
            <Link to="/login" style={{ color: 'var(--alex-primary)', fontWeight: 600, textDecoration: 'none' }}>
              Kembali ke halaman masuk
            </Link>
          </div>
        </div>
      </main>
    </div>
  );
}

function Field(props: { label: string; value: string; onChange: (e: { target: { value: string } }) => void; type?: string; required?: boolean; autoFocus?: boolean }) {
  return (
    <label style={labelStyle}>
      {props.label}
      <input
        value={props.value}
        onChange={props.onChange}
        type={props.type ?? 'text'}
        required={props.required}
        autoFocus={props.autoFocus}
        style={inputStyle}
      />
    </label>
  );
}

const labelStyle = { fontSize: 'var(--alex-fs-caption)', color: 'var(--alex-text-secondary)', fontWeight: 600 } as const;

const inputStyle = {
  display: 'block',
  width: '100%',
  padding: '10px 12px',
  marginTop: 6,
  border: '1px solid var(--alex-border)',
  borderRadius: 'var(--alex-control-radius)',
  fontSize: 'var(--alex-fs-body)',
  color: 'var(--alex-text-primary)',
  boxSizing: 'border-box',
  fontFamily: 'inherit',
} as const;

const primaryButtonStyle = {
  padding: '11px 12px',
  border: 'none',
  borderRadius: 'var(--alex-control-radius)',
  background: 'var(--alex-primary)',
  color: '#fff',
  fontSize: 'var(--alex-fs-body)',
  fontWeight: 700,
  cursor: 'pointer',
  width: '100%',
} as const;
